/**
 * Who teaches this class — one row per (teacher, section, subject).
 *
 * The school's own act of configuration: "Ms Rao teaches 10-B chemistry". Not an
 * invitation and not a request — an administrator says it, and every student in
 * the room is taught by her from that moment, including the ones who join next
 * term.
 *
 * **Assignment is per SECTION.** A teacher stands in front of a room, and a room
 * is a section; whether "the whole grade's maths teacher" is a thing worth
 * expressing is a question to answer when a school asks it, not one to leave
 * standing as an extra scope on every screen. Class-scope rows already recorded
 * are still listed and still removable — they are live, and hiding them would
 * leave someone teaching a room nobody could see.
 *
 * **Several teachers per (section, subject).** Co-taught sets, a specialist
 * beside the class teacher, a handover in progress — all ordinary, and each named
 * teacher holds the full relation, not a fraction of one.
 *
 * On a public directory record the table is shown with the reason assignment is
 * refused. A missing control reads as a bug and sends people looking for it
 * elsewhere; a stated reason teaches the model in the one moment somebody is
 * asking the question.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, DataTable, Modal, SearchFilterBar, Select,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import {
  schoolsApi, type SchoolClass, type AssignedTeacher, type TenantTeacher,
} from '../../api/schoolsApi';

/** The four subjects a teaching relation can name. A History teacher has nowhere
 *  to land — `student_teacher_map` cannot express one — so the picker does not
 *  offer subjects the assignment would silently drop. */
const TEACHABLE = ['maths', 'physics', 'chemistry', 'biology'] as const;
type Teachable = typeof TEACHABLE[number];

/** One teaching relation, flattened out of the per-scope maps the API returns. */
interface TeachingRow {
  key: string;
  scope: 'class' | 'section';
  /** Section id, or the class id for a class-scope row. */
  scopeId: string;
  scopeName: string;
  subject: string;
  teacherId: string;
  teacherName: string;
}

export function TeachersTab({ cls, subjects, schoolName }: {
  cls: SchoolClass; subjects: string[]; schoolName?: string;
}) {
  const qc = useQueryClient();
  const subjectLabel = useSubjectLabel();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState | undefined>({ sortBy: 'section', sortOrder: 'asc' });
  const [assigning, setAssigning] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['class-teachers', cls.tenantId, cls.id],
    queryFn: () => schoolsApi.getClassTeachers(cls.tenantId, cls.id),
  });
  const { data: staffData } = useQuery({
    queryKey: ['tenant-teachers', cls.tenantId],
    queryFn: () => schoolsApi.listTenantTeachers(cls.tenantId),
    enabled: !!data?.assignable,
  });
  const staff = useMemo<TenantTeacher[]>(() => staffData?.teachers ?? [], [staffData]);

  /** Only the subjects this class teaches AND a relation can express (TS-4f). */
  const offered = useMemo<Teachable[]>(() => {
    const own = new Set(subjects.length ? subjects : TEACHABLE);
    return TEACHABLE.filter(s => own.has(s));
  }, [subjects]);

  const sections = useMemo(
    () => (data?.assignments ?? []).filter(a => a.scope === 'section'),
    [data],
  );

  const rows = useMemo<TeachingRow[]>(() => (data?.assignments ?? []).flatMap(a =>
    Object.entries(a.subjectTeachers).flatMap(([subject, teachers]) =>
      (teachers as AssignedTeacher[]).map(t => ({
        key: `${a.scope}:${a.id}:${subject}:${t.id}`,
        scope: a.scope,
        scopeId: a.id,
        scopeName: a.scope === 'class' ? 'Whole class' : a.name,
        subject,
        teacherId: t.id,
        teacherName: t.name,
      })))), [data]);

  /**
   * Write one scope's whole map back — the API replaces it, so a single
   * add or remove is a read-modify-write of the map the row came from.
   */
  const write = useMutation({
    mutationFn: async (writes: Array<{
      scope: 'class' | 'section'; scopeId: string; next: Record<string, string[]>;
    }>) => {
      // Sequential rather than concurrent. Each section is its own document, so
      // there is no contention to lose — but one add naming four sections should
      // stop at the first refusal instead of leaving the reader to work out
      // which three of the four went in.
      for (const { scope, scopeId, next } of writes) {
        const body = Object.fromEntries(Object.entries(next).filter(([, ids]) => ids.length > 0));
        if (scope === 'class') await schoolsApi.setClassTeachers(cls.tenantId, cls.id, body);
        else await schoolsApi.setSectionTeachers(cls.tenantId, cls.id, scopeId, body);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['class-teachers', cls.tenantId, cls.id] }),
  });

  /** The scope's current map as plain ids, which is what a write sends. */
  const mapOf = (scope: 'class' | 'section', scopeId: string): Record<string, string[]> =>
    Object.fromEntries(Object.entries(
      (data?.assignments ?? []).find(a => a.scope === scope && a.id === scopeId)?.subjectTeachers ?? {},
    ).map(([subject, teachers]) => [subject, (teachers as AssignedTeacher[]).map(t => t.id)]));

  const unassign = (row: TeachingRow) => {
    const next = mapOf(row.scope, row.scopeId);
    next[row.subject] = (next[row.subject] ?? []).filter(id => id !== row.teacherId);
    write.mutate([{ scope: row.scope, scopeId: row.scopeId, next }]);
  };

  /** One teacher, one subject, every section they take it in — the reshuffle a
   *  school actually does ("Ms Rao has 10-A and 10-C this year") in one act
   *  rather than as N passes through the same dialog. Each section is still its
   *  own relation; this only saves the repetition. */
  const assign = (sectionIds: string[], subject: string, teacherId: string) => {
    write.mutate(sectionIds.map(sectionId => {
      const next = mapOf('section', sectionId);
      next[subject] = [...new Set([...(next[subject] ?? []), teacherId])];
      return { scope: 'section' as const, scopeId: sectionId, next };
    }));
  };

  /** Sections this exact relation already covers. With every section on screen
   *  at once, an add that changes nothing has to say so up front — otherwise it
   *  reads as a failed write. */
  const alreadyTaught = (subject: string, teacherId: string) => new Set(
    rows.filter(r => r.scope === 'section' && r.subject === subject && r.teacherId === teacherId)
      .map(r => r.scopeId),
  );

  /** Every filter offers what the rows actually hold — a filter for a value
   *  nothing has is a dead end. Section is the exception: an empty section is
   *  exactly what an administrator filters for. */
  const filterDefs = useMemo<FilterDef[]>(() => [
    {
      key: 'teacherId',
      placeholder: 'All teachers',
      options: [...new Map(rows.map(r => [r.teacherId, r.teacherName])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ value: id, label: name })),
    },
    {
      key: 'scopeId',
      placeholder: 'All sections',
      options: sections.map(s => ({ value: s.id, label: s.name })),
    },
    {
      key: 'subject',
      placeholder: 'All subjects',
      options: offered.map(s => ({ value: s, label: subjectLabel(s) })),
    },
  ], [rows, sections, offered, subjectLabel]);

  const visible = useMemo(() => {
    const term = (filters.search ?? '').trim().toLowerCase();
    return rows.filter(r => {
      if (filters.teacherId && r.teacherId !== filters.teacherId) return false;
      if (filters.scopeId && r.scopeId !== filters.scopeId) return false;
      if (filters.subject && r.subject !== filters.subject) return false;
      if (!term) return true;
      return [r.teacherName, r.scopeName, subjectLabel(r.subject)]
        .some(v => v.toLowerCase().includes(term));
    });
  }, [rows, filters, subjectLabel]);

  const columns: ColumnDef<TeachingRow>[] = [
    {
      key: 'teacher',
      header: 'Teacher',
      sortable: true,
      sortValue: r => r.teacherName.toLowerCase(),
      render: r => r.teacherName,
    },
    {
      key: 'section',
      header: 'Section',
      sortable: true,
      sortValue: r => r.scopeName.toLowerCase(),
      // A class-scope row is legacy, and says so: it cannot be created here any
      // more, and it teaches more students than its neighbours in the table.
      render: r => (r.scope === 'class'
        ? <span title="Assigned to the whole class — this scope is no longer offered">Whole class</span>
        : r.scopeName),
    },
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      sortValue: r => subjectLabel(r.subject).toLowerCase(),
      render: r => subjectLabel(r.subject),
    },
    ...(data?.assignable ? [{
      key: 'actions',
      header: '',
      render: (r: TeachingRow) => (
        <span className="school-row-actions">
          <button
            type="button"
            className="school-row-icon school-row-icon--danger"
            disabled={write.isPending}
            aria-label={`Unassign ${r.teacherName} from ${r.scopeName} ${subjectLabel(r.subject)}`}
            title="Unassign — the teacher stops teaching this room's students"
            onClick={() => unassign(r)}
          >×</button>
        </span>
      ),
    } satisfies ColumnDef<TeachingRow>] : []),
  ];

  if (isLoading) return <p className="school-hint">Loading…</p>;
  if (error) return <p className="school-error">{(error as Error).message}</p>;
  if (!data) return null;

  const canAssign = data.assignable && sections.length > 0 && offered.length > 0;

  return (
    <section className="school-section">
      <div className="school-toolbar">
        <h2 className="school-section-title">Teachers</h2>
        {data.assignable && (
          <Button variant="primary" size="sm" disabled={!canAssign} onClick={() => setAssigning(true)}>
            + Add teacher
          </Button>
        )}
      </div>

      {!data.assignable && <p className="school-hint">{data.reason}</p>}
      {data.assignable && offered.length === 0 && (
        <p className="school-hint">
          This class does not teach any subject a teacher can be assigned for.
        </p>
      )}
      {data.assignable && offered.length > 0 && sections.length === 0 && (
        <p className="school-hint">
          Teachers are assigned to a section. Add one on the class's General tab first.
        </p>
      )}

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search teacher, section or subject…"
        hideFetch
      />

      <DataTable<TeachingRow>
        columns={columns}
        data={visible}
        rowKey={r => r.key}
        bordered
        sort={sort}
        onSortChange={setSort}
        emptyMessage={rows.length === 0
          ? 'Nobody is assigned to teach this class yet.'
          : 'No assignment matches those filters.'}
      />

      {write.isError && <p className="school-error">{(write.error as Error).message}</p>}

      {assigning && (
        <AssignTeacherDialog
          sections={sections.map(s => ({ id: s.id, name: s.name }))}
          subjects={offered}
          subjectLabel={subjectLabel}
          staff={staff}
          pending={write.isPending}
          schoolName={schoolName}
          grade={cls.grade}
          alreadyTaught={alreadyTaught}
          onClose={() => setAssigning(false)}
          onAssign={(sectionIds, subject, teacherId) => {
            assign(sectionIds, subject, teacherId);
            setAssigning(false);
          }}
        />
      )}
    </section>
  );
}

/**
 * Name a relation: this subject, this teacher, these sections.
 *
 * Asked in the order a school decides it. The subject is the fixed thing — a
 * timetable is drawn up subject by subject — the teacher is who was hired or
 * moved for it, and only then does "which rooms" have an answer. Section first
 * (the old order) asked the one question that depends on both of the others
 * before either had been said.
 *
 * Several sections at once because the answer is routinely more than one, and
 * re-opening the same dialog per room made a single decision look like four.
 */
function AssignTeacherDialog({
  sections, subjects, subjectLabel, staff, pending, schoolName, grade, alreadyTaught,
  onClose, onAssign,
}: {
  sections: Array<{ id: string; name: string }>;
  subjects: readonly Teachable[];
  subjectLabel: (s: string) => string;
  staff: TenantTeacher[];
  pending: boolean;
  schoolName?: string;
  grade: string;
  alreadyTaught: (subject: string, teacherId: string) => Set<string>;
  onClose: () => void;
  onAssign: (sectionIds: string[], subject: string, teacherId: string) => void;
}) {
  const [subject, setSubject] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const ready = !!subject && !!teacherId && sectionIds.length > 0;

  // Only meaningful once both are chosen; before that every section is offered
  // plain, because "already assigned" has no subject or teacher to be true of.
  const covered = subject && teacherId ? alreadyTaught(subject, teacherId) : new Set<string>();

  return (
    <Modal isOpen onClose={onClose} title="Add a teacher" size="sm">
      <div className="school-form">
        {/* Which room this is being said about. The dialog is opened from a tab
            inside one class, and the page header that names the school and the
            grade is behind the overlay by the time anyone is filling this in. */}
        <p className="school-hint">
          {schoolName ? `${schoolName} · ` : ''}Grade {grade}
        </p>
        <label>Subject
          <Select value={subject} onChange={e => setSubject(e.target.value)}>
            <option value="">— pick a subject —</option>
            {subjects.map(s => <option key={s} value={s}>{subjectLabel(s)}</option>)}
          </Select>
        </label>
        <label>Teacher
          <Select value={teacherId} onChange={e => setTeacherId(e.target.value)}>
            <option value="">{staff.length ? '— pick a teacher —' : 'No teaching staff in this tenant'}</option>
            {staff.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        </label>
        {/* Checkboxes on the page, not a dropdown. `.modal-body` scrolls, so an
            absolutely-positioned menu opening in the last field of a short
            dialog is clipped by it — and a class has a handful of sections, few
            enough that showing them all costs nothing and lets the
            already-assigned ones be read without opening anything. */}
        <div className="school-form-field">
          <span>Sections</span>
          {sections.length === 0
            ? <p className="school-hint">This class has no sections recorded.</p>
            : (
              <div className="school-checkbox-list">
                {sections.map(sec => (
                  <label key={sec.id} className="school-checkbox">
                    <input
                      type="checkbox"
                      checked={sectionIds.includes(sec.id)}
                      onChange={e => setSectionIds(ids => (e.target.checked
                        ? [...ids, sec.id]
                        : ids.filter(id => id !== sec.id)))}
                    />
                    <span>{sec.name}</span>
                    {covered.has(sec.id) && (
                      <span className="school-hint">already assigned</span>
                    )}
                  </label>
                ))}
              </div>
            )}
        </div>
        <div className="school-modal-actions">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!ready || pending}
            onClick={() => onAssign(sectionIds, subject, teacherId)}>
            {pending ? 'Adding…' : 'Add teacher'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
